import { HttpError } from "@/shared/api/errors"
import type { VerifyPurchaseRequest } from "@/shared/api/generated-backend"
import { zVerifyPurchaseErrorResponse } from "@/shared/api/generated-backend/zod.gen"
import type { PurchaseVerification } from "../model/types"
import { sendPurchaseVerification } from "./client"

const RETRY_COOLDOWN_MS = 15 * 60_000
// With `client_action: "none"`, the server keeps the transaction open, for
// example when an SBP payment is in progress. Thus the client tries again
// after a short time, not after 15 minutes.
const PENDING_RETRY_COOLDOWN_MS = 30_000

const blocked = new Map<string, { error: unknown; until: number }>()

export function verifyPurchase(
	body: VerifyPurchaseRequest,
): Promise<PurchaseVerification> {
	const key = body.transaction_id

	dropExpiredBlocks()
	const block = blocked.get(key)
	if (block) return Promise.reject(block.error)

	return sendPurchaseVerification(body).catch((error: unknown) => {
		if (isFinalRejection(error)) {
			let cooldown = RETRY_COOLDOWN_MS
			if (keepsTransactionOpen(error)) cooldown = PENDING_RETRY_COOLDOWN_MS
			blocked.set(key, { error, until: Date.now() + cooldown })
		}
		throw error
	})
}

export function clearVerificationCooldown(): void {
	blocked.clear()
}

function dropExpiredBlocks(): void {
	const now = Date.now()
	for (const [key, block] of blocked) {
		if (block.until <= now) blocked.delete(key)
	}
}

function isFinalRejection(error: unknown): error is HttpError {
	if (!(error instanceof HttpError)) return false
	if (error.status === 408 || error.status === 429) return false
	return error.status >= 400 && error.status < 500
}

function keepsTransactionOpen(error: HttpError): boolean {
	const body = zVerifyPurchaseErrorResponse
		.pick({ client_action: true })
		.safeParse(error.info)
	return body.success && body.data.client_action === "none"
}
